import React, { Component, Fragment, ReactNode } from 'react';
import { createFragmentContainer, graphql, RelayProp } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import createDesignProject from '../mutations/createDesignProject';
import LazyLoad from 'react-lazy-load';
import { Button } from 'dibs-elements/exports/Button';
import { GridCol } from 'dibs-elements/exports/Grid';
import { PAGE_TYPE_PROJECTS } from '../constants';

import styles from './OverviewProjects.scss';
import { DisplayDesignProjectTile } from '../components/DesignProjectTile/DisplayDesignProjectTile';
import { EditDesignProjectTile } from '../components/DesignProjectTile/EditDesignProjectTile';
import { NewProjectTile } from '../components/DesignProjectTile/NewProjectTile';
import { logUnexpectedNullGraphQLError } from '../../helpers';
import { getProjectEditorUrl } from '../helpers/getProjectEditorUrl';
import { PageType } from '../constants';

import { OverviewProjects_tradeFirm } from './__generated__/OverviewProjects_tradeFirm.graphql';

type Props = {
    adminHost: string;
    tradeFirm: OverviewProjects_tradeFirm;
    onChangePage: (pageType: PageType) => void;
    isClient: boolean;
    isAdminMode: boolean;
    isEditMode: boolean;
    relay: RelayProp;
};

export const MAX_OVERVIEW_TILES = 4;

export class OverviewProjectsComponent extends Component<Props> {
    createDesignProject: () => Promise<void> = async () => {
        const { isAdminMode, relay, tradeFirm, adminHost } = this.props;
        const firmId = tradeFirm?.serviceId;
        const windowRef = isAdminMode ? window.open('about:blank', '_blank') : null;

        if (!firmId) {
            logUnexpectedNullGraphQLError('tradeFirm', 'serviceId');
            return;
        }

        const response = await createDesignProject(relay.environment, { firmId: parseInt(firmId) });
        const project = response?.createDesignProject?.project;
        const projectEditorUrl = getProjectEditorUrl(project || null, adminHost, isAdminMode);

        if (projectEditorUrl && isAdminMode && windowRef) {
            windowRef.location.href = projectEditorUrl;
        } else if (isAdminMode && windowRef) {
            windowRef.close();
        } else if (projectEditorUrl) {
            window.location.assign(projectEditorUrl);
        }
    };

    renderTiles: () => ReactNode[] = () => {
        const { adminHost, isAdminMode, tradeFirm, isEditMode } = this.props;
        const editProjects = tradeFirm?.publicProfile?.editModeDesignProjects || [];
        const liveProjects = tradeFirm?.publicProfile?.liveDesignProjects || [];
        let tiles = isEditMode
            ? editProjects
                  .slice(0, 4)
                  .map((project, index) => (
                      <EditDesignProjectTile
                          adminHost={adminHost}
                          isAdminMode={isAdminMode}
                          key={`overview-project-tile-${project?.serviceId || index}`}
                          designProject={project}
                          urlLabel={tradeFirm?.publicProfile?.urlLabel || ''}
                      />
                  ))
            : liveProjects.slice(0, 4).map((project, index) => (
                  <GridCol
                      tabletPortrait={6}
                      desktop={3}
                      key={`overview-project-tile-${project?.serviceId || index}`}
                  >
                      <LazyLoad offset={200} height={242}>
                          <DisplayDesignProjectTile designProject={project} />
                      </LazyLoad>
                  </GridCol>
              ));

        if (isEditMode) {
            const additionalTilesLength = MAX_OVERVIEW_TILES - tiles.length;

            if (additionalTilesLength > 0) {
                const tmpArray = new Array(additionalTilesLength);
                const fillTiles = tmpArray
                    .fill(null) // don't care about the value, it will be clobbered below
                    .map((tile, index) => (
                        <NewProjectTile
                            isClient={this.props.isClient}
                            createDesignProject={this.createDesignProject}
                            key={`overview-new-project-tile-${index}`}
                        />
                    ));
                tiles = tiles.concat(fillTiles);
            }
        }

        return tiles;
    };

    render(): ReactNode {
        const tiles = this.renderTiles();
        if (!tiles.length) {
            return null;
        }

        return (
            <Fragment>
                <div className={styles.title}>
                    <FormattedMessage
                        id="abt.designProfile.overview.projects.title"
                        defaultMessage="Projects"
                    />
                </div>

                <div className={`rowFlex ${styles.tileWrapper}`}>{tiles}</div>
                <div className={styles.seeAllWrapper}>
                    <div className={styles.seeAllButton}>
                        <Button
                            dataTn="view-all-projects"
                            type="secondary"
                            onClick={() => {
                                this.props.onChangePage(PAGE_TYPE_PROJECTS);
                                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                            }}
                        >
                            <FormattedMessage
                                id="abt.designProfile.overview.projects.viewAll"
                                defaultMessage="View All Projects"
                            />
                        </Button>
                    </div>
                </div>
            </Fragment>
        );
    }
}

export const OverviewProjects = createFragmentContainer(OverviewProjectsComponent, {
    tradeFirm: graphql`
        fragment OverviewProjects_tradeFirm on TradeFirmType {
            serviceId
            publicProfile {
                urlLabel
                liveDesignProjects: designProjectsCover @skip(if: $isEditMode) {
                    ...DisplayDesignProjectTile_designProject
                    serviceId
                    status
                    visibility
                }
                editModeDesignProjects: designProjects(
                    visibility: ["Y", "N"]
                    status: ["ACTIVE", "PENDING"]
                ) @include(if: $isEditMode) {
                    ...EditDesignProjectTile_designProject
                    serviceId
                    status
                    visibility
                }
            }
        }
    `,
});
